import {
  SortDirection,
} from '@mesa-labs/mesa-api/dist/types';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DownloadButton,
  LoadingScreen,
  Pagination,
  PaginationDropdown,
  Table,
  Typography,
  utils,
} from '@mesa-labs/mesa-ui';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { ColumnProp } from '@mesa-labs/mesa-ui/dist/components/Table';
import { DefaultPaginationDropdownLimits } from '@mesa-labs/mesa-ui/dist/components/Pagination';

import { PaginationContainer } from '../common/Pagination';
import {
  asExportFilter,
  useLazyExportAllExternalClientVendorsAsFacilitatorQuery,
  useGetExternalClientAsFacilitatorQuery,
  useGetExternalClientVendorsAsFacilitatorQuery,
} from '../../redux/api/facilitators';
import { useDispatch, useSelector } from '../../redux/hooks';
import {
  updateVendorsLimit, updateVendorsPage, updateVendorsSortDirection, updateVendorsSortField, updateVendorsTotalPages,
} from '../../redux/slices/clients';
import { Header } from '../common/Header';
import { CardContainer, CardTitle, CardTitleContainer, CardsContainer, CardText, CardTitleLine } from '../common/Cards';
import { DashboardEvents, trackEvent } from '../../analytics';

const ClientPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TitleContainer = styled.div`
  align-items: center;
  display: flex;  
  justify-content: space-between;
  padding: 20px 24px;
`;

const TableTitle = styled.div`
  color: #181D27;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
`;

export const VendorDetailsPageControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const WeightedAverageInvoiceTimelineTitle = styled.h4`
  color: #181D27;
  font-size: 16px;
  font-weight: 600;
  line-height: 28px;
`;

const Legend = styled.div`
  align-items: center;
  display: flex;  
  gap: 12px;
`;

const LegendItem = styled.div`
  align-items: center;
  display: flex;  
  gap: 8px;
`;

const LegendMarker = styled.div<{ color: string }>`
  background: ${(props) => props.color};
  border-radius: 50%;
  height: 8px;
  width: 8px;
`;

const LegendTitle = styled.span`
  color: #535862;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
`;

const VendorColumns: ColumnProp[] = [
  {
    key: 'externalVendorName',
    label: 'Name',
    sortField: 'externalVendorName',
  },
  {
    key: 'externalVendorId',
    label: 'Supplier ID(s)',
    sortField: 'externalVendorId',
  },
  {
    key: 'internalActivatedAt',
    label: 'Enablement Date',
    sortField: 'internalActivatedAt',
    type: 'date',
  },
  {
    key: 'servicedInvoiceSpendT12m',
    label: 'Client Mesa Funded Spend',
    sortField: 'servicedInvoiceSpendT12m',
    type: 'currency',
  },
];

function ClientPage(): React.ReactElement | null {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: externalClientId } = useParams();

  const accessToken = useSelector((state) => state.auth.accessToken);
  const partnerId = useSelector((state) => state.auth.partnerId);
  const vendorsSortField = useSelector((state) => state.clients.vendorsSortField);
  const vendorsSortDirection = useSelector((state) => state.clients.vendorsSortDirection);
  const vendorsPage = useSelector((state) => state.clients.vendorsPage) || 1;
  const vendorsTotalPages = useSelector((state) => state.clients.vendorsTotalPages);
  const vendorsLimit = useSelector((state) => state.clients.vendorsLimit) || DefaultPaginationDropdownLimits[0];

  const {
    data: client,
  } = useGetExternalClientAsFacilitatorQuery({
    accessToken: accessToken!,
    partnerId: partnerId!,
    externalClientId: externalClientId!,
  }, { skip: !partnerId || !externalClientId });

  const vendorFilter = useMemo(() => ({
    sortField: vendorsSortField,
    sortDirection: vendorsSortDirection,
    page: vendorsPage,
    limit: (typeof vendorsLimit === 'number' ? vendorsLimit : vendorsLimit.value),
  }), [vendorsSortField, vendorsSortDirection, vendorsPage, vendorsLimit]);

  const {
    data: {
      data: vendors,
      total: totalVendors,
    } = {},
  } = useGetExternalClientVendorsAsFacilitatorQuery({
    accessToken: accessToken!,
    partnerId: partnerId!,
    externalClientId: externalClientId!,
    ...vendorFilter,
  }, { skip: !partnerId || !externalClientId });

  const [exportAllExternalClientVendorsAsFacilitator] = useLazyExportAllExternalClientVendorsAsFacilitatorQuery();

  useEffect(() => {
    if (totalVendors !== undefined) {
      dispatch(updateVendorsTotalPages(Math.ceil(totalVendors / (typeof vendorsLimit === 'number' ? vendorsLimit : vendorsLimit.value))));
    }
  }, [totalVendors, vendorsLimit]);

  const vendorRows = useMemo(() => (vendors || []).map((vendor) => ({
    id: `${vendor.partnerId}:${vendor.externalVendorId}`,
    internalVendorId: vendor.internalVendorId,
    externalVendorName: vendor.externalVendorName,
    externalVendorId: vendor.externalVendorId,
    internalActivatedAt: vendor.internalActivatedAt,
    servicedInvoiceSpendT12m: vendor.servicedInvoiceSpendT12m,
    currency: 'USD',
  })), [vendors]);

  const handleExport = async () => {
    const { data } = await exportAllExternalClientVendorsAsFacilitator({ partnerId: partnerId!, externalClientId: externalClientId!, ...asExportFilter(vendorFilter) });
    return data;
  }

  const timelineSeries: ApexAxisChartSeries = useMemo(() => {
    if (!client) {
      return [];
    }

    return [
      {
        name: 'Weighted Average Approval Time',
        data: [{ x: 'Time (Days)', y: Math.round(client.weightedAverageApprovalTime || 0) }]
      },
      {
        name: 'Weighted Average Import Lateness',
        data: [{ x: 'Time (Days)', y: Math.round(client.weightedAverageImportLateness || 0) }]
      },
      {
        name: 'Weighted Average Pre-Float Period',
        data: [{ x: 'Time (Days)', y: Math.round(client.weightedAveragePreFloatPeriod || 0) }]
      },
      {
        name: 'Weighted Average Float Period',
        data: [{ x: 'Time (Days)', y: Math.round(client.weightedAverageFloatPeriod || 0) }]
      },
    ];
  }, [client]);

  const stackedHorizontalBarChartOptions = (): ApexOptions => useMemo(() => ({
    colors: ['#D5CDFF', '#B7A6FF', '#9473FF', '#743BFF'],
    chart: {
      type: 'bar',
      stacked: true,
      stackType: 'normal',
      width: '100%',
      toolbar: {
        show: false,
      },
    },
    dataLabels: { enabled: false },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        dataLabels: {
          total: {
            enabled: true,
            offsetY: 0,
            style: {
              fontSize: '13px',
              fontWeight: 900
            }
          }
        }
      },
    },
    legend: {
      show: false,
    },
    markers: {
      size: 0,
      hover: {
        sizeOffset: 6,
      },
    },
    grid: {
      borderColor: '#f1f1f1',
    },
    stroke: {
      width: 2,
      colors: ['#F6F8FA'],
    },
  }), []);

  useEffect(() => {
    trackEvent(DashboardEvents.SourcingClientsDetailView);
  }, []);

  return (
    <ClientPageContainer>
      <Header>{(client && client.externalClientName) || 'Client Details'}</Header>

      <CardsContainer>
        <CardContainer>
          {client && (
            <>
              <CardTitleContainer>
                <CardTitleLine color="#335CFF" />
                <CardTitle>Total Mesa Suppliers</CardTitle>
              </CardTitleContainer>
              <CardText>{client?.totalInternalVendors}</CardText>
            </>
          ) || <LoadingScreen />}
        </CardContainer>

        <CardContainer>
          {client && (
            <>
              <CardTitleContainer>
                <CardTitleLine color="#12B76A" />
                <CardTitle>Total Mesa Funded Suppliers</CardTitle>
              </CardTitleContainer>
              <CardText>{client?.totalServicedVendorsT12m}</CardText>
            </>
          ) || <LoadingScreen />}
        </CardContainer>

        <CardContainer>
          {client && (
            <>
              <CardTitleContainer>
                <CardTitleLine color="#FDB71C" />
                <CardTitle>Total Mesa Funded Spend</CardTitle>
              </CardTitleContainer>
              <CardText>{utils.formatCurrency(client?.totalServicedInvoiceSpendT12m || 0, 'USD')}</CardText>
            </>
          ) || <LoadingScreen />}
        </CardContainer>
      </CardsContainer>

      <CardContainer>
        <CardTitleContainer justifyContent="space-between">
          <WeightedAverageInvoiceTimelineTitle>Weighted Average Invoice Timeline</WeightedAverageInvoiceTimelineTitle>

          <Legend>
            <LegendItem>
              <LegendMarker color="#D5CDFF" />
              <LegendTitle>Average Approval Time</LegendTitle>
            </LegendItem>
            <LegendItem>
              <LegendMarker color="#B7A6FF" />
              <LegendTitle>Average Import Lateness</LegendTitle>
            </LegendItem>
            <LegendItem>
              <LegendMarker color="#9473FF" />
              <LegendTitle>Average Pre-Float Period</LegendTitle>
            </LegendItem>
            <LegendItem>
              <LegendMarker color="#743BFF" />
              <LegendTitle>Average Float Period</LegendTitle>
            </LegendItem>
          </Legend>
        </CardTitleContainer>

        <ReactApexChart options={stackedHorizontalBarChartOptions()} series={timelineSeries} type="bar"
          height={200} />
      </CardContainer>

      <TitleContainer>
        <TableTitle>Enabled Suppliers</TableTitle>

        <VendorDetailsPageControlsContainer>
          <DownloadButton
            text="Download CSV"
            data={() => handleExport()}
            fileName="client_suppliers.csv"
            display="purple"
          />
        </VendorDetailsPageControlsContainer>
      </TitleContainer>
      {vendors !== undefined && (
        <Table
          columns={VendorColumns}
          rows={vendorRows}
          currentSortColumn={vendorsSortField}
          currentSortDirection={vendorsSortDirection}
          setSortField={(field: string) => dispatch(updateVendorsSortField(field))}
          setSortDirection={(direction: SortDirection) => dispatch(updateVendorsSortDirection(direction))}
          onRowClick={(row: Record<string, unknown>) => navigate(`/suppliers/${row.externalVendorId}`)}
          customStyles={{
            columnsPadding: '24px',
            columnsBorderRadius: 0,
            paginationPadding: '0px',
            rowPadding: '0px 24px',
            columnColor: '#717680',
            columnFontSize: '12px',
            columnFontWeight: 600,
          }}
        />
      )}
      {vendors === undefined && (
        <LoadingScreen />
      )}

      {!!vendorsTotalPages && (
        <PaginationContainer>
          <PaginationDropdown
            selectedLimit={(typeof vendorsLimit === 'number' ? DefaultPaginationDropdownLimits.find((l) => l.value === vendorsLimit) || DefaultPaginationDropdownLimits[0] : vendorsLimit)}
            onChange={(item) => {
              dispatch(updateVendorsLimit(item));
              dispatch(updateVendorsPage(1));
            }}
          />
          <Typography.BodySmallBold>
            Total of
            {' '}
            {totalVendors !== undefined ? totalVendors : '...'}
            {' '}
            Supplier(s)
          </Typography.BodySmallBold>
          <Pagination
            currentPage={vendorsPage}
            totalPages={vendorsTotalPages}
            onPrev={() => {
              dispatch(updateVendorsPage(vendorsPage - 1));
            }}
            onNext={() => {
              dispatch(updateVendorsPage(vendorsPage + 1));
            }}
          />
        </PaginationContainer>
      )}

    </ClientPageContainer>
  );
}

export default ClientPage;
